import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useRef, useState } from 'react';
import { InvoiceHeaderRow } from './InvoiceHeaderRow';
import { InvoiceItem, InvoiceItemRow } from './InvoiceItemRow';
import css from './InvoiceList.module.scss';

type InvoiceListProps = {
  invoices: InvoiceItem[];
};

export const InvoiceList: React.FC<InvoiceListProps> = ({ invoices }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());

  const virtualizer = useVirtualizer({
    count: invoices.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 60,
  });

  const entries = virtualizer.getVirtualItems();

  const handleToggleSelection = (invoiceId: string) => {
    setSelectedInvoices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  const handleSelectAllToggle = () => {
    if (selectedInvoices.size === invoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(invoices.map((invoice) => invoice.id)));
    }
  };

  const isAllSelected = selectedInvoices.size === invoices.length && invoices.length > 0;
  const isPartiallySelected = selectedInvoices.size > 0 && selectedInvoices.size < invoices.length;

  return (
    <div className={css.invoiceListContainer}>
      <InvoiceHeaderRow
        totalInvoices={invoices.length}
        selectedInvoices={selectedInvoices.size}
        onSelectAllToggle={handleSelectAllToggle}
        isAllSelected={isAllSelected}
        isPartiallySelected={isPartiallySelected}
      />
      <GradientScrollDiv
        ref={scrollRef}
        innerStyle={{ height: '400px', maxHeight: '400px' }}
        className={css.gradientScrollDiv}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            position: 'relative',
            width: '100%',
          }}
        >
          {entries.map((virtualItem) => {
            const invoice = invoices[virtualItem.index];
            return (
              <InvoiceItemRow
                key={invoice.id}
                invoice={invoice}
                isSelected={selectedInvoices.has(invoice.id)}
                onToggleSelection={handleToggleSelection}
                height={virtualItem.size}
                start={virtualItem.start}
              />
            );
          })}
        </div>
      </GradientScrollDiv>
    </div>
  );
};
